import type { GoogleAdsConfig } from "@/config/env";
import { analyticsActive, watchAnalytics } from "../analytics";
import { isToolPath } from "../routes";
import { googleAdapter } from "./google";
import { metaAdapter } from "./meta";
import { openaiAdapter } from "./openai";
import { createRelay } from "./relay";
import { signupId } from "./signupId";
import type { AdAdapter, Purchase } from "./types";

/**
 * The ads the app measures with, behind one rule (lib/ads/relay.ts): a
 * sign-up or a purchase is counted only while product analytics runs
 * (lib/analytics.ts) and only where the place allows it (`placeAds`). Each
 * platform has an adapter of its own: google.ts, openai.ts, meta.ts.
 *
 * With no platform configured (a local build, the sample, the tests)
 * nothing loads.
 */

export { PURCHASE_WAIT_MS } from "./relay";
export type { Conversion, Purchase } from "./types";

/** Each platform's account, or null for one that is not configured. */
export interface AdsConfig {
    google: GoogleAdsConfig | null;
    openai: string | null;
    meta: string | null;
}

const relay = createRelay({ active: analyticsActive, watch: watchAnalytics });

/**
 * Whether ad tags may load on a page at this address: everywhere but the
 * tool pages, where a visitor pastes a resume. A tag that has started keeps
 * running after a move inside the app, so every link into the tools loads
 * a new page, and the entry point asks this before it configures anything.
 */
export function adsMayLoadOn(pathname: string): boolean {
    return !isToolPath(pathname);
}

/**
 * What the entry point does: configures the ads for a page loaded at this
 * address, unless it is a tool page, which configures nothing.
 */
export function configureAdsOn(pathname: string, config: AdsConfig): void {
    if (adsMayLoadOn(pathname)) configureAds(config);
}

/** Remembers the accounts, and follows analytics from now on. Loads nothing. */
export function configureAds(config: AdsConfig): void {
    const adapters: AdAdapter[] = [];
    if (config.google) adapters.push(googleAdapter(config.google));
    if (config.openai) adapters.push(openaiAdapter(config.openai));
    if (config.meta) adapters.push(metaAdapter(config.meta));
    relay.configure(adapters);
}

/** Whether the place allows ad measurement: known to be outside those that ask first, and no Global Privacy Control. */
export function placeAds(allowed: boolean): void {
    relay.place(allowed);
}

/** Whether the ads are counting now. */
export function adsActive(): boolean {
    return relay.active();
}

/**
 * Counts a purchase, now when measurement runs, or once it starts within
 * {@link PURCHASE_WAIT_MS}, since the consent is read after the page opens.
 */
export function trackPurchase(purchase: Purchase): void {
    relay.track(purchase);
}

/**
 * Counts a new account's sign-up, as {@link trackPurchase} counts a
 * purchase, with no value. Its id is a SHA-256 of the account id, so the
 * account is counted once and no platform sees the id itself.
 */
export function trackSignup(userId: string): void {
    if (!relay.configured()) return;
    void signupId(userId)
        .then((transactionId) =>
            relay.track({ kind: "signup", value: null, currency: null, transactionId }),
        )
        // No Web Crypto (an insecure page): nothing is counted.
        .catch(() => undefined);
}

/** For the tests: as the page loads, with nothing started. */
export function resetAdsForTests(): void {
    relay.reset();
}
